import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Shield } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse, UserProfile } from '@hirenfire/shared';

interface PublicProfile {
  displayName: string;
  bio: string | null;
  avatar: string | null;
  country: string | null;
  languages: string[];
  skills: string[];
  availability: string | null;
  hourlyRate: number | null;
  role: string;
  kycVerified: boolean;
  memberSince: string;
}

export function PublicProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const res = await api.get<ApiResponse<PublicProfile>>(`/profile/${userId}`);
        if (res.data.data) setProfile(res.data.data);
      } catch {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    }
    fetchProfile();
  }, [userId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="space-y-6 animate-fade-in max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <Card>
          <CardBody className="py-16 text-center">
            <p className="text-gray-500 text-lg">Profile not found or is private</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <Card>
        <CardBody>
          <div className="flex items-start gap-6">
            <Avatar
              src={profile.avatar}
              name={profile.displayName}
              size="xl"
            />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">{profile.displayName}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={profile.role === 'AGENCY' ? 'purple' : 'primary'}>
                  {profile.role}
                </Badge>
                {profile.kycVerified && (
                  <Badge variant="success">
                    <Shield className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              {profile.bio && (
                <p className="text-gray-400 mt-3">{profile.bio}</p>
              )}

              <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-400">
                {profile.country && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.country}
                  </span>
                )}
                {profile.availability && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {profile.availability}
                  </span>
                )}
                <span className="text-gray-500">
                  Member since {new Date(profile.memberSince).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {profile.skills.length > 0 && (
        <Card>
          <CardHeader>Skills</CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((skill) => (
                <Badge key={skill} variant="primary">{skill}</Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {profile.languages.length > 0 && (
        <Card>
          <CardHeader>Languages</CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {profile.languages.map((lang) => (
                <Badge key={lang}>{lang}</Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {profile.hourlyRate && (
        <Card>
          <CardHeader>Rate</CardHeader>
          <CardBody>
            <p className="text-2xl font-bold text-white">${profile.hourlyRate}/hr</p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
